import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import type { CustomRouteComponentProps } from 'react-router-dom';
import { compose } from 'redux';
import type { InjectedFormProps, FormAction } from 'redux-form';
import { reduxForm } from 'redux-form';

import type { CompanyViewModel } from 'ab-viewModels/company.viewModel';

import { COMPANY_EDIT } from 'af-constants/reduxForms';

import { Button } from '@acceligentllc/storybook';

import CustomModal from 'af-components/CustomModal';
import EditCompanyForm from 'af-components/SharedForms/Company/BasicInfoForm';
import { CompanyFormModel } from 'af-components/SharedForms/Company/BasicInfoForm/form';
import { validate } from 'af-components/SharedForms/Company/BasicInfoForm/validations';
import SubmitButton from 'af-components/SubmitButton';

import * as CompanyActions from 'af-actions/companies';

type FormProps = InjectedFormProps<CompanyFormModel, FormOwnProps>;

interface CompanySettingsRouteProps {
	orgAlias: string;
}

interface OwnProps extends CustomRouteComponentProps<CompanySettingsRouteProps> {
	company: CompanyViewModel;
	isVisible: boolean;
	toggleEdit: () => void;
	changeField: (form: string, field: string, value: string | number) => FormAction;
	initialValues: FormProps['initialValues'];
}

type FormOwnProps = OwnProps & ConnectedProps<typeof connector>;
type Props = FormOwnProps & FormProps;

const CompanyEdit: React.FC<Props> = (props) => {
	const {
		toggleEdit,
		initialValues: { name },
		editCompany,
		company,
		reset,
		handleSubmit,
		isVisible,
		invalid,
		submitting,
	} = props;

	const [image, setImage] = React.useState<string | File | null>(null);
	const [shouldDeleteImage, setShouldDeleteImage] = React.useState<boolean>(false);

	const submit = React.useCallback(async (form: CompanyFormModel) => {
		form.image = image;
		form.deleteImage = shouldDeleteImage;

		form.nameChanged = name !== form.name;

		const rm = CompanyFormModel.toEditRM(form);
		await editCompany(company.id, rm);
		toggleEdit();
	}, [company.id, editCompany, image, name, shouldDeleteImage, toggleEdit]);

	const onClose = React.useCallback(() => {
		reset();
		toggleEdit();
	}, [reset, toggleEdit]);

	return (
		<CustomModal
			closeModal={onClose}
			modalStyle="info"
			showModal={isVisible}
			size="lg"
		>
			<CustomModal.Header
				closeModal={onClose}
				title="Edit Company Settings"
			/>
			<CustomModal.Body padding="none">
				<EditCompanyForm
					{...props}
					formName={COMPANY_EDIT}
					image={image}
					setImage={setImage}
					setShouldDeleteImage={setShouldDeleteImage}
				/>
			</CustomModal.Body>
			<CustomModal.Footer>
				<Button
					label="Cancel"
					onClick={onClose}
					style="secondary"
				/>
				<SubmitButton
					disabled={invalid}
					label="Save"
					onClick={handleSubmit(submit)}
					reduxFormSubmitting={submitting}
					submitKey={COMPANY_EDIT}
				/>
			</CustomModal.Footer>
		</CustomModal>
	);
};

function mapDispatchToProps() {
	return {
		editCompany: CompanyActions.editCompany,
	};
}

const connector = connect(null, mapDispatchToProps());

const enhance = compose<React.ComponentClass<OwnProps>>(
	React.memo,
	connector,
	reduxForm<CompanyFormModel, FormOwnProps>({ form: COMPANY_EDIT, validate, enableReinitialize: true })
);

export default enhance(CompanyEdit);
